import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  getSessionToken,
  setSessionToken,
  clearSessionToken,
  setUnauthorizedHandler,
} from "@/lib/app";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("checking"); // "checking" | "authenticated" | "denied"
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [tidoContext, setTidoContext] = useState(null); // { idp, rok, mesiac, type } from source page
  const [lockedStoreId, setLockedStoreId] = useState(null); // resolved store_id for the IDP
  const bootRef = useRef(false); // StrictMode-safe: prevent double landing calls
  const navigate = useNavigate();

  const logout = useCallback(async () => {
    // Best-effort: SSO sessions can be revoked server-side; JWT dev tokens are stateless.
    try { await api.post("/sso/logout"); } catch { /* ignore */ }
    clearSessionToken();
    setUser(null);
    setStatus("denied");
  }, []);

  // ── Developer local login ────────────────────────────────────
  const devLogin = useCallback(async (username, password) => {
    setError(null);
    const { data } = await api.post("/auth/login", { username, password });
    setSessionToken(data.access_token);
    setUser({
      user_id: data.user.id,
      user_name: data.user.username,
      role: data.user.role,
      kind: "user",
    });
    setStatus("authenticated");
    return {};
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(async () => {
      // Any 401 → drop the token and show the login page.
      clearSessionToken();
      setUser(null);
      setStatus("denied");
    });
  }, []);

  useEffect(() => {
    if (bootRef.current) return;      // StrictMode fires effects twice in dev
    bootRef.current = true;
    const run = async () => {
      const params = new URLSearchParams(window.location.search);

      // Extract TiDo cross-app context (IDP=center_id, ROK=year, MESIAC=month).
      const ctx = {
        idp: params.get("IDP") || params.get("idp"),
        rok: params.get("ROK") || params.get("rok"),
        mesiac: params.get("MESIAC") || params.get("mesiac"),
        type: params.get("_type"),
      };
      const hasCtx = ctx.idp || ctx.rok || ctx.mesiac;
      let effectiveCtx = null;
      if (hasCtx) {
        effectiveCtx = ctx;
        setTidoContext(ctx);
        try { sessionStorage.setItem("tido_ctx", JSON.stringify(ctx)); } catch { /* ignore */ }
      } else {
        try {
          const stored = sessionStorage.getItem("tido_ctx");
          if (stored) {
            effectiveCtx = JSON.parse(stored);
            setTidoContext(effectiveCtx);
          }
        } catch { /* ignore */ }
      }

      const domena = params.get("domena") || params.get("DOMENA");
      const idSpravcu = params.get("id_spravcu") || params.get("ID_SPRAVCU");

      // Any landing from a TiDo link (?domena=…) must discard any stale
      // developer JWT from a previous tab session — otherwise the previous
      // "developer" role leaks into the new prevádzka/admin scope.
      if (domena && (idSpravcu || ctx.idp)) {
        clearSessionToken();
      }

      // 0a) PREVÁDZKA landing — ?IDP=X&domena=Y (no id_spravcu)
      // Single-store, tenant-verified via integration_key + api_client_name.
      // Auto-creates the store on first visit and runs the full uvitanie
      // data pull — the operator never has to onboard stores by hand.
      if (domena && ctx.idp && !idSpravcu) {
        try {
          const rokParam = ctx.rok ? parseInt(ctx.rok, 10) : null;
          const mesiacParam = ctx.mesiac ? parseInt(ctx.mesiac, 10) : null;
          const body = {
            domena,
            idp: parseInt(ctx.idp, 10),
          };
          if (rokParam) body.rok = rokParam;
          if (mesiacParam) body.mesiac = mesiacParam;
          const { data } = await api.post("/sso/prevadzka-landing", body);
          setSessionToken(data.session_token);
          setUser({
            user_id: data.user.user_id,
            user_name: data.user.user_name,
            role: data.user.role,               // "prevadzka"
            company_id: data.company.id,
            company_name: data.company.name,
            locked_store_id: data.user.locked_store_id,
            locked_tido_center_id: data.user.locked_tido_center_id,
            kind: "prevadzka-landing",
          });
          const storeJustCreated = !!(data.store && data.store.needs_uvitanie);
          if (storeJustCreated) {
            // Fresh store — jump DIRECTLY to /tido-uvitanie which will do
            // the chunked 13-month sync and then navigate to store detail.
            // We use `navigate` explicitly rather than relying on state
            // side-effects, so no race with other components' useEffects.
            setLockedStoreId(null);
            try { sessionStorage.removeItem("tido_locked_store_id"); } catch { /* ignore */ }
            setStatus("authenticated");
            // Keep IDP/ROK/MESIAC/domena in the URL — TidoUvitanie reads
            // them from params to know what/where to sync.
            navigate(`/tido-uvitanie${window.location.search}`, { replace: true });
            return;
          }
          setLockedStoreId(data.user.locked_store_id);
          try {
            sessionStorage.setItem("tido_locked_store_id", data.user.locked_store_id);
          } catch { /* ignore */ }
          // Store already existed — drop `domena` from address bar.
          params.delete("domena"); params.delete("DOMENA");
          const cleaned = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
          window.history.replaceState({}, "", cleaned);
          setStatus("authenticated");
          return;
        } catch (e) {
          setError(e.response?.data?.detail || e.message || "Prihlásenie zlyhalo (prevádzka)");
          setStatus("denied");
          return;
        }
      }

      // 0b) TiDo admin/supervisor landing — ?domena=X&id_spravcu=Y (no IDP)
      if (domena && idSpravcu && !ctx.idp) {
        try {
          const { data } = await api.post("/sso/admin-landing", {
            domena,
            id_spravcu: parseInt(idSpravcu, 10),
          });
          setSessionToken(data.session_token);
          setUser({
            user_id: data.user.user_id,
            user_name: data.user.user_name,
            role: data.user.role,
            allowed_groups: data.user.allowed_groups || [],
            company_id: data.company.id,
            company_name: data.company.name,
            kind: "admin-landing",
          });
          // Admin-landing overrides any stale IDP context from a previous tab session.
          try {
            sessionStorage.removeItem("tido_ctx");
            sessionStorage.removeItem("tido_locked_store_id");
          } catch { /* ignore */ }
          setTidoContext(null);
          setLockedStoreId(null);
          // Clean URL — drop domena + id_spravcu.
          params.delete("domena"); params.delete("DOMENA");
          params.delete("id_spravcu"); params.delete("ID_SPRAVCU");
          const cleaned = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
          // Land admin/supervisor DIRECTLY inside their client (never on
          // the developer-only client list).
          const target = `/klienti/${data.company.id}`;
          window.history.replaceState({}, "", `${target}${cleaned.includes("?") ? cleaned.slice(cleaned.indexOf("?")) : ""}`);
          setStatus("authenticated");
          return;
        } catch (e) {
          setError(e.response?.data?.detail || e.message || "Prihlásenie zlyhalo (admin landing)");
          setStatus("denied");
          return;
        }
      }

      // Resolve locked_store_id if IDP is present (only when we have a token — call is guarded).
      const resolveIdp = (effectiveCtx && effectiveCtx.idp && getSessionToken());
      if (resolveIdp) {
        try {
          const cached = sessionStorage.getItem("tido_locked_store_id");
          if (cached) setLockedStoreId(cached);
        } catch { /* ignore */ }
        api.get(`/stores/by-tido-center/${encodeURIComponent(effectiveCtx.idp)}`)
          .then(({ data }) => {
            setLockedStoreId(data.id);
            try { sessionStorage.setItem("tido_locked_store_id", data.id); } catch { /* ignore */ }
          })
          .catch(() => { /* store not yet created — /tido-uvitanie will handle it */ });
      }

      // 1) SSO token in URL — exchange for a session (backward compatible).
      const ssoToken = params.get("sso_token");
      if (ssoToken) {
        try {
          const { data } = await api.post("/sso/verify-token", { sso_token: ssoToken });
          setSessionToken(data.session_token);
          setUser({ ...data.user, kind: "sso" });
          params.delete("sso_token");
          const cleaned = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
          window.history.replaceState({}, "", cleaned);
          setStatus("authenticated");
          return;
        } catch (e) {
          setError(e.response?.data?.detail || e.message || "SSO overenie zlyhalo");
          setStatus("denied");
          return;
        }
      }

      // 2) Existing token in localStorage — try `/auth/me` (works for both dev JWT and SSO).
      const existing = getSessionToken();
      if (!existing) {
        setStatus("denied");
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        // Normalize shape (dev users vs SSO sessions)
        if (data.kind === "user") {
          setUser({
            user_id: data.user_id,
            user_name: data.username,
            role: data.role,
            kind: "user",
          });
        } else {
          setUser({ ...data, kind: "sso" });
        }
        setStatus("authenticated");
      } catch (e) {
        clearSessionToken();
        setError(e.response?.data?.detail || "Session vypršala");
        setStatus("denied");
      }
    };
    run();
  }, []);

  const clearTidoContext = useCallback(() => {
    setTidoContext(null);
    setLockedStoreId(null);
    try {
      sessionStorage.removeItem("tido_ctx");
      sessionStorage.removeItem("tido_locked_store_id");
    } catch { /* ignore */ }
  }, []);

  const lockedIdp = tidoContext?.idp || null;

  return (
    <AuthContext.Provider value={{
      status, user, error, logout,
      devLogin,
      tidoContext, clearTidoContext,
      lockedIdp, lockedStoreId, setLockedStoreId,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
